import { createAPIClient } from '@/utils/network-utils/createAPIClient';

import Service from '@/services/Service';
import MemoryCache from '@/utils/MemoryCache';
import { traceable } from '@/services/utils/decorators/traceable';
import siteCached from '@/services/utils/siteCached';
import { TimeScale } from '@/utils/time-utils';
import { isNullish, isNullOrEmpty } from '@/utils/null-utils';
import LoggerService from '@/services/isomorphic/LoggerService';
import { assertError } from '@/utils/error-utils';
import { DataModelTarget } from '@/services/isomorphic/integrations/BuilderService/schemas';

import ServerContentService from '@/services/serverless/ServerContentService';
import ConfigurationService, { type Config } from '../ConfigurationService';
import ContentServiceMock from './ContentServiceMock';
import { CMSService, type Content } from './Content';
import type {
  ContentOptions,
  IContentOptions,
  IModelContentOptions
} from './IContentOptions';
import { PreviewService } from '../PreviewService';
import { EnvironmentService } from '../EnvironmentService';
import { ExpectedContentNotFoundError } from './errors/ExpectedContentNotFoundError';
import { HomepageContentNotFoundError } from './errors/HomepageContentNotFoundError';

/**
 * Abstracts functionalities related to Content.
 *
 * This service handles client-side memory caching and delegates content fetching to:
 * - API route (when on client)
 * - ServerContentService (when on server).
 */
export class ContentService extends Service {
  private client = createAPIClient({ baseURL: '/api/content' });

  /**
   * Returns the relevant CMS integration and makes it publicly accessible.
   * @returns The CMS service.
   */
  @siteCached
  public get integration(): CMSService {
    return this.config.getSetting('service').value as CMSService;
  }

  /**
   * The Content config.
   * @returns A `Config<'content'>`.
   */
  @siteCached
  private get config(): Config<'content'> {
    return ConfigurationService.getConfig('content');
  }

  /**
   * Client-side memory cache for content.
   * @returns The MemoryCache instance.
   */
  @siteCached
  private get cache(): MemoryCache<Promise<Content>> {
    const revalidate = this.config.getSetting('clientRevalidate').value;
    return new MemoryCache(revalidate, TimeScale.Seconds);
  }

  /**
   * Adds the content to the client-side cache.
   * @param options - The content options used to fetch content.
   * @param content - The content promise to be cached.
   */
  private addToCache(
    options: IContentOptions,
    content: Promise<Content>
  ): void {
    const revalidate = this.config.getSetting('clientRevalidate').value;
    const revalidateFail = this.config.getSetting(
      'dynamicRevalidateFail'
    ).value;
    const cacheKey = this.getCacheKey(options);
    const isHomePage = options.id === '/';

    this.cache.add(cacheKey, content);

    /* eslint-disable-next-line no-void -- We want to schedule
        this async logic without awaiting it. */
    void content
      .then(
        (c) => {
          if (options.isExpected && isNullish(c.data))
            throw new Error(
              `Content for ${options.id} was expected but not found.`
            );

          this.cache.setTTL(cacheKey, revalidate);
          return undefined;
        },
        () => {
          throw new Error(`Content not found for ${options.id}.`);
        }
      )
      .catch((err) => {
        let error = assertError(err);
        error = isHomePage
          ? new HomepageContentNotFoundError(error.message)
          : new ExpectedContentNotFoundError(error.message);
        LoggerService.error(error);

        // Use a lower revalidate number if the content fails to load.
        this.cache.setTTL(cacheKey, revalidateFail);
      });
  }

  /**
   * Gets the cache key for the content.
   * @param options - The content options.
   * @returns A string that represents the cache key for this content request.
   */
  private getCacheKey(options: IContentOptions | IModelContentOptions): string {
    return `${options.id}-${options.type}-${options.tag}`;
  }

  /**
   * Fetches the content from the CMS.
   *
   * On the server: delegates to ServerContentService (with distributed caching).
   * On the client: checks memory cache, then calls API route.
   *
   * @param options - The content options used to fetch content.
   * @returns The content of the type of the current CMS.
   */
  @traceable('ContentService.getContent')
  public async getContent(options: ContentOptions): Promise<Content> {
    // On the server, delegate to ServerContentService
    if ((typeof window === "undefined")) {
      // Preview mode bypasses server cache
      if (PreviewService.isPreviewActive) {
        return ServerContentService.getContentWithoutCache(options);
      }
      return ServerContentService.getContent(options);
    }

    // Client-side: check memory cache first (unless preview mode)
    const cacheKey = this.getCacheKey(options);

    if (!PreviewService.isPreviewActive && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Fetch from API route
    const contentPromise = this.client
      .post<Content>('', { ...options })
      .then((res) => res.data);

    // Add to client cache (unless preview mode)
    if (!PreviewService.isPreviewActive) {
      this.addToCache(options, contentPromise);
    }

    return contentPromise;
  }

  /**
   * Gets the metadata for given content options.
   * @param options - The content options used to fetch content.
   * @returns The metadata for the content.
   */
  public async getContentMetaData(
    options: ContentOptions
  ): Promise<{
    title: string;
    description: string;
    robots: { index: boolean };
  }> {
    const metadataConfig = ConfigurationService.getConfig('metadata');
    const defaultTitle = metadataConfig.getSetting('home.title').value;
    const defaultDescription =
      metadataConfig.getSetting('home.description').value;
    const index = metadataConfig.getSetting('home.robots.index').value;

    const content = await this.getContent(options);

    // Extract title and description from content if available
    let title = defaultTitle;
    let description = defaultDescription;

    if (
      content.type === DataModelTarget.CategoryModel ||
      content.type === DataModelTarget.ProductModel ||
      content.type === DataModelTarget.PageModel
    ) {
      const categoryTitle = content.dataModel?.title;
      const categoryDescription = content.dataModel?.description;
      title = isNullOrEmpty(categoryTitle)
        ? defaultTitle
        : (categoryTitle ?? defaultTitle);
      description = isNullOrEmpty(categoryDescription)
        ? defaultDescription
        : (categoryDescription ?? defaultDescription);
    }

    return { title, description, robots: { index } };
  }
}

export default ContentService.withMock(new ContentServiceMock(ContentService));
